import type { RNG } from "./prng";

// Re-export RNG so a kernel can import its full contract (Kernel, MathShim, RNG) from one
// module. RNG's implementation lives in ./prng; kernels only need the type.
export type { RNG } from "./prng";

// The deterministic-math shim. M0 maps to native Math (fine for pure-arithmetic kernels
// like pi, which Spike 02 proved are bitwise-portable across engines). M3 swaps this for
// fdlibm-via-WASM so transcendental kernels (log/exp/sin/cos) also reproduce across engines.
export interface MathShim {
  sqrt(x: number): number;
  log(x: number): number;
  exp(x: number): number;
  sin(x: number): number;
  cos(x: number): number;
  pow(x: number, y: number): number;
}

export const nativeMath: MathShim = Math;

/** A tunable numeric parameter a kernel exposes to the UI. The chosen values become part of
 *  the run's deterministic inputs — a result depends on (kernel, seed, N, params), and params
 *  are encoded in the permalink so the run still reproduces bitwise. Numeric-only by design:
 *  trivial to validate, clamp, encode, and keep engine-portable. */
export interface ParamSpec {
  /** Stable key — used in chunk()'s params map and the permalink. Keep it short + URL-safe. */
  readonly key: string;
  /** Human label for the control. */
  readonly label: string;
  /** Value used when the user hasn't set one (and the gallery card's starting point). */
  readonly default: number;
  readonly min?: number;
  readonly max?: number;
  /** Slider/number-input step. Also defines the value grid params are snapped to. */
  readonly step?: number;
  /** Unit suffix shown next to the control (e.g. "$", "%", "yr"). */
  readonly unit?: string;
  /** One-line explanation shown in the control's tooltip. */
  readonly hint?: string;
}

/** Resolved parameter values passed to a kernel: key → number. */
export type Params = Record<string, number>;

/**
 * A Kernel is an embarrassingly-parallel Monte Carlo simulation expressed as:
 *  - `chunk`: run `count` iterations with a seeded RNG and the chosen params, return a partial.
 *  - `reduce`: deterministically combine partials (given in fixed chunk-index order)
 *              into a final scalar result plus a sample series for the histogram.
 *
 * Reducing in fixed chunk order — not completion order — is what makes the result
 * independent of worker count and scheduling, hence reproducible.
 */
export interface Kernel<P = unknown> {
  readonly id: string;
  readonly label: string;
  /** true => uses only IEEE-bitwise-specified ops + rng (+ math shim). RFC-0008 §5. */
  readonly verifiable: boolean;
  /** One-line gallery description shown under the kernel name. Optional. */
  readonly blurb?: string;
  /** Unit/label for the headline result (e.g. "π estimate", "call price", "t/ha"). */
  readonly resultUnit?: string;
  /** Suggested iteration count for this kernel. Heavier kernels (SIR) default lower than
   *  π. The UI seeds the N input from this; falls back to a global default when absent. */
  readonly defaultN?: number;
  /** Iterations per logical chunk. Lighter-per-iteration kernels use a large value (π);
   *  heavier ones (SIR) use a small one so even a few-thousand-iteration run still yields
   *  enough chunks to plot a distribution. Like everything else, the chunk *count* is a
   *  function of (kernel, N) only — never of worker count — so results stay reproducible. */
  readonly chunkSize?: number;
  /** Tunable inputs the UI renders as controls. Optional — a kernel with no knobs omits it.
   *  Values are validated/clamped to each spec before reaching chunk(), so the kernel can
   *  trust them. */
  readonly params?: readonly ParamSpec[];
  chunk(count: number, rng: RNG, math: MathShim, params: Params): P;
  reduce(orderedPartials: P[]): { result: number; samples: number[] };
}

/** Resolve a kernel's params: start from defaults, override with provided values, clamp to
 *  [min,max] and snap to step. Deterministic and total — the same inputs always yield the
 *  same map, so it's safe to run identically on the main thread and in every worker. */
export function resolveParams(
  specs: readonly ParamSpec[] | undefined,
  provided: Params | undefined,
): Params {
  const out: Params = {};
  if (!specs) return out;
  for (const spec of specs) {
    let v = provided?.[spec.key];
    if (v === undefined || !Number.isFinite(v)) v = spec.default;
    if (spec.min !== undefined) v = Math.max(spec.min, v);
    if (spec.max !== undefined) v = Math.min(spec.max, v);
    if (spec.step !== undefined && spec.step > 0) {
      const base = spec.min ?? 0;
      v = base + Math.round((v - base) / spec.step) * spec.step;
      // Re-clamp after snapping (rounding can push past max).
      if (spec.max !== undefined) v = Math.min(spec.max, v);
      if (spec.min !== undefined) v = Math.max(spec.min, v);
    }
    out[spec.key] = v;
  }
  return out;
}

/** Hash the IEEE-754 bits of a float to the `_bits` hex string used as the result hash. */
export function resultBits(value: number): string {
  const buf = new ArrayBuffer(8);
  new DataView(buf).setFloat64(0, value, false);
  const bytes = new Uint8Array(buf);
  let hex = "";
  for (const b of bytes) hex += b.toString(16).padStart(2, "0");
  return hex;
}

/** Stable content hash of a kernel's identity (FNV-1a) — the kernel hash in a permalink. */
export function kernelHash(k: Kernel): string {
  let h = 0x811c9dc5;
  const src = `${k.id}|${k.verifiable}`;
  for (let i = 0; i < src.length; i++) {
    h ^= src.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0).toString(16).padStart(8, "0");
}
